/**
 * AddressForm Component
 *
 * Controlled form for editing a structured address (streetName, buildingNumber,
 * city, postalCode, countryCode) matching the `Address` GraphQL type. Stateless —
 * the caller owns `value` and submit lifecycle. The country field uses
 * `CountrySelector` and can be hidden via `hideCountry` when the parent already
 * renders a standalone country picker.
 *
 * Optional `errors` are bucketed by `path` and rendered below the matching
 * field via `FieldErrorList`; issues without a known field path surface as
 * a top-level banner at the top of the form.
 *
 * Uses `useTranslation('modules.modals.address')` for all labels.
 */
import * as React from 'react';
import { useMemo } from 'react';
import { Input } from '../../../components/inputs';
import { InputWrapper } from '../../../components/InputWrapper';
import { CountrySelector } from '../../../components/selects/CountrySelector';
import { AlertBanner } from '../../validation/AlertBanner';
import { FieldErrorList } from '../../validation/FieldErrorList';
import { ErrorEntry } from '../../../components/errors';
import { useTranslation } from '../../../providers/UIProvider';
import type { ValidationIssue } from '../../../types';
import type { AddressFormState } from './utils';

export type { AddressFormState } from './utils';

export interface AddressFormProps {
  value: AddressFormState;
  onFieldChange: (field: keyof AddressFormState, next: string) => void;
  disabled?: boolean;
  autoFocusStreet?: boolean;
  /**
   * When `true` the country dropdown is not rendered.
   * Use this when the parent already shows a standalone `CountrySelector`
   * so the field is not duplicated.
   */
  hideCountry?: boolean;
  /** External validation issues bucketed by `path`. */
  errors?: ValidationIssue[];
  /** Dot-path prefix for `data-hover-field` attributes (e.g. `"supplierAddress"`). */
  fieldPrefix?: string;
  /** Prefix for field element ids, so several address forms can share a page. */
  idPrefix?: string;
}

const FIELD_KEYS = ['streetName', 'buildingNumber', 'city', 'postalCode', 'countryCode'] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

function bucketIssues(errors: ValidationIssue[] | undefined) {
  const byField: Record<FieldKey, ValidationIssue[]> = {
    streetName: [],
    buildingNumber: [],
    city: [],
    postalCode: [],
    countryCode: []
  };
  const top: ValidationIssue[] = [];
  for (const issue of errors ?? []) {
    const path = issue.path ?? '';
    if ((FIELD_KEYS as readonly string[]).includes(path)) {
      byField[path as FieldKey].push(issue);
    } else {
      top.push(issue);
    }
  }
  return { byField, top };
}

export const AddressForm: React.FC<AddressFormProps> = ({
  value,
  onFieldChange,
  disabled = false,
  autoFocusStreet = true,
  hideCountry = false,
  errors,
  fieldPrefix,
  idPrefix = 'addr'
}) => {
  const t = useTranslation('modules.modals.address');

  const { byField, top } = useMemo(() => bucketIssues(errors), [errors]);

  const hf = (field: string) =>
    fieldPrefix ? { 'data-hover-field': `${fieldPrefix}.${field}` } : {};

  return (
    <>
      {top.map((issue, i) => (
        <AlertBanner
          key={`top-${i}`}
          variant={issue.severity === 'error' ? 'error' : 'warning'}
          title={<ErrorEntry issue={issue} showActual />}
        />
      ))}

      <div className='grid grid-cols-[1fr_7rem] gap-md'>
        <div {...hf('streetName')}>
          <InputWrapper
            label={t('fields.streetName')}
            htmlFor={`${idPrefix}-street`}
            labelSize='sm'
          >
            <Input
              id={`${idPrefix}-street`}
              type='text'
              value={value.streetName}
              onChange={e => onFieldChange('streetName', e.target.value)}
              height='lg'
              autoFocus={autoFocusStreet}
              disabled={disabled}
            />
            <FieldErrorList issues={byField.streetName} />
          </InputWrapper>
        </div>

        <div {...hf('buildingNumber')}>
          <InputWrapper
            label={t('fields.buildingNumber')}
            htmlFor={`${idPrefix}-building`}
            labelSize='sm'
          >
            <Input
              id={`${idPrefix}-building`}
              type='text'
              value={value.buildingNumber}
              onChange={e => onFieldChange('buildingNumber', e.target.value)}
              height='lg'
              disabled={disabled}
            />
            <FieldErrorList issues={byField.buildingNumber} />
          </InputWrapper>
        </div>
      </div>

      <div className='grid grid-cols-[1fr_7rem] gap-md'>
        <div {...hf('city')}>
          <InputWrapper label={t('fields.city')} htmlFor={`${idPrefix}-city`} labelSize='sm'>
            <Input
              id={`${idPrefix}-city`}
              type='text'
              value={value.city}
              onChange={e => onFieldChange('city', e.target.value)}
              height='lg'
              disabled={disabled}
            />
            <FieldErrorList issues={byField.city} />
          </InputWrapper>
        </div>

        <div {...hf('postalCode')}>
          <InputWrapper
            label={t('fields.postalCode')}
            htmlFor={`${idPrefix}-postal`}
            labelSize='sm'
          >
            <Input
              id={`${idPrefix}-postal`}
              type='text'
              value={value.postalCode}
              onChange={e => onFieldChange('postalCode', e.target.value)}
              height='lg'
              disabled={disabled}
            />
            <FieldErrorList issues={byField.postalCode} />
          </InputWrapper>
        </div>
      </div>

      {!hideCountry && (
        <div {...hf('countryCode')}>
          <InputWrapper
            label={t('fields.countryCode')}
            htmlFor={`${idPrefix}-country`}
            labelSize='sm'
            hint={t('fields.countryCodeHint')}
          >
            <CountrySelector
              id={`${idPrefix}-country`}
              value={value.countryCode}
              unselectedOptionLabel={t('fields.countryCodeNone')}
              onChange={code => onFieldChange('countryCode', code)}
              disabled={disabled}
              height='lg'
              isSearchable
            />
            <FieldErrorList issues={byField.countryCode} />
          </InputWrapper>
        </div>
      )}
    </>
  );
};

AddressForm.displayName = 'AddressForm';
